import axios from 'axios';
import { uploadPromoImage, deletePromoImage } from '../utils/storage';

export interface PromotionStats {
  promo_id: string;
  promo_name: string;
  total_trxs: number;
  total_consumers: number;
  total_clients: number;
  average_trx_per_client: number;
  trxs_per_day: Array<{
    created_at: string;
    count: string;
  }>;
  trxs_per_zip: Array<{
    locality: string;
    total: string;
    percentage: string;
  }>;
  new_consumers: Array<{
    date: string;
    qty: number;
  }>;
  top_10_clients: Array<{
    client_id: string;
    client_name: string;
    qty: string;
  }>;
  top_10_consumers: Array<{
    consumer_id: string;
    consumer_name: string;
    qty: string;
  }>;
}

// export const API_BASE_URL = 'https://club-coca-cola-api.vercel.app/api/v1/back';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:9999/api/v1/back';
const AUTH_TOKEN = 'FeMsA@2024';

export interface ActivePromotion {
  id: number;
  img: string;
  title: string;
}

export interface Promotion {
  id: number;
  img: string;
  title: string;
  code?: string;
  client_points: number;
  consumer_limit: number;
  client_limit: number;
  start_date: string; // Formatted as 'DD/MM/YYYY'
  end_date: string;   // Formatted as 'DD/MM/YYYY'
  status: 'activo' | 'inactivo';
  terms: string;
  channel: string;
  gec: string;
  recipients: string;
}

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Authorization': `Bearer ${AUTH_TOKEN}`,
    'Content-Type': 'application/json',
  },
});

export const getPromotions = async (): Promise<Promotion[]> => {
  try {
    const response = await api.get('/promotions');
    return response.data;
  } catch (error) {
    console.error('Error fetching promotions:', error);
    throw error;
  }
};

export const getActivePromotions = async (): Promise<ActivePromotion[]> => {
  try {
    const response = await api.get('/promotions/active');
    return response.data;
  } catch (error) {
    console.error('Error fetching active promotions:', error);
    throw error;
  }
};

export const getPromotionStats = async (promoId: string): Promise<PromotionStats> => {
  try {
    const response = await api.get(`/dashboard/${promoId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching stats for promotion ${promoId}:`, error);
    throw error;
  }
};

export const getGeneralStats = async (): Promise<PromotionStats> => {
  try {
    const response = await api.get('/dashboard');
    return response.data;
  } catch (error) {
    console.error('Error fetching general dashboard stats:', error);
    throw error;
  }
};

export const getPromotionById = async (id: number): Promise<Promotion> => {
  try {
    const response = await api.get(`/promotions/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching promotion ${id}:`, error);
    throw error;
  }
};

export interface StatusUpdateResponse {
  success: boolean;
  res?: string;
}

export const updatePromotionStatus = async (id: number, status: 'activo' | 'inactivo'): Promise<StatusUpdateResponse> => {
  try {
    const response = await api.put('/promotions/change_status', { id, status });
    return { success: true, ...response.data };
  } catch (error: any) {
    console.error(`Error updating promotion ${id} status:`, error);
    return { 
      success: false, 
      res: error.response?.data?.res || 'Ocurrió un error al actualizar el estado de la promoción' 
    };
  }
};

export const updatePromotion = async (id: number, promotionData: Partial<Promotion>, imageFile?: File): Promise<Promotion> => {
  try {
    // If there's a new image file, upload it first
    if (imageFile) {
      // If there was a previous image, delete it
      if (promotionData.img) {
        await deletePromoImage(promotionData.img);
      }
      // Upload new image
      const imageUrl = await uploadPromoImage(imageFile);
      promotionData.img = imageUrl;
    }
    
    // Include the ID in the request body instead of the URL
    const requestData = {
      ...promotionData,
      id: id // Add the ID to the request body
    };
    
    const response = await api.put('/promotions', requestData);
    return response.data;
  } catch (error) {
    console.error(`Error updating promotion ${id}:`, error);
    throw error;
  }
};

export interface ClientVariable {
  id: string;
}

export interface ClientVariablesResponse {
  channels: ClientVariable[];
  gec: ClientVariable[];
}

export const getClientVariables = async (): Promise<ClientVariablesResponse> => {
  try {
    const response = await api.get('/variables');
    return response.data;
  } catch (error) {
    console.error('Error fetching client variables:', error);
    // Return empty arrays if there's an error
    return { channels: [], gec: [] };
  }
};

export interface CreatePromotionData {
  img: string;
  title: string;
  code?: string;
  client_points: number;
  consumer_limit: number;
  client_limit: number;
  start_date: string; // YYYY-MM-DD
  end_date: string;   // YYYY-MM-DD
  terms: string;
  channel: string;
  gec: string;
  recipients: string;
}

export const createPromotion = async (promotionData: CreatePromotionData, imageFile: File): Promise<Promotion> => {
  try {
    // First upload the image if it exists
    let imageUrl = promotionData.img;
    if (imageFile) {
      imageUrl = await uploadPromoImage(imageFile);
    }

    // Then create the promotion with the image URL
    const response = await api.post('/promotions', {
      ...promotionData,
      img: imageUrl,
    });

    return response.data;
  } catch (error) {
    console.error('Error creating promotion:', error);
    throw error;
  }
};

export const deletePromotion = async (id: number): Promise<{ success: boolean; message?: string }> => {
  try {
    await api.delete(`/promotions/${id}`);
    return { success: true };
  } catch (error: any) {
    console.error(`Error deleting promotion ${id}:`, error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Error al eliminar la promoción' 
    };
  }
};
